<template>
    <div>
        <div class="p-6 pb-0">
            <BreadCrumbs :navigation="navigation" />
        </div>

        <div class="p-6">
            <div class="bg-white shadow-md rounded-lg p-6 border border-gray-200 w-full mx-auto">
                <div class="flex justify-between items-center mb-6 border-b pb-4">
                    <h1 class="text-2xl font-bold text-gray-800">Storico Rilevazioni</h1>
                    <div v-if="(peopleSmart || devOps) && isAdmin">
                        <!-- Desktop: buttons -->
                        <div class="hidden sm:flex gap-2">
                            <button 
                                @click="setMode('activities')" 
                                :disabled="loading"
                                :class="['px-4 py-2 rounded-md text-sm font-medium transition-colors', mode === 'activities' ? 'bg-blue-600 text-white' : 'bg-gray-100 text-gray-700 hover:bg-gray-200', loading ? 'opacity-50 cursor-not-allowed' : '']"
                            >
                                Rilevazioni
                            </button>
                            <template v-if="peopleSmart">
                                <button 
                                    @click="setMode('pres')" 
                                    :disabled="loading"
                                    :class="['px-4 py-2 rounded-md text-sm font-medium transition-colors', mode === 'pres' ? 'bg-blue-600 text-white' : 'bg-gray-100 text-gray-700 hover:bg-gray-200', loading ? 'opacity-50 cursor-not-allowed' : '']"
                                >
                                    PS - Presenze
                                </button>
                                <button 
                                    @click="setMode('gius')" 
                                    :disabled="loading"
                                    :class="['px-4 py-2 rounded-md text-sm font-medium transition-colors', mode === 'gius' ? 'bg-blue-600 text-white' : 'bg-gray-100 text-gray-700 hover:bg-gray-200', loading ? 'opacity-50 cursor-not-allowed' : '']"
                                >
                                    PS - Giustificativi
                                </button>
                                <button 
                                    @click="setMode('anag')" 
                                    :disabled="loading"
                                    :class="['px-4 py-2 rounded-md text-sm font-medium transition-colors', mode === 'anag' ? 'bg-blue-600 text-white' : 'bg-gray-100 text-gray-700 hover:bg-gray-200', loading ? 'opacity-50 cursor-not-allowed' : '']"
                                >
                                    PS - Anagrafiche
                                </button>
                            </template>
                            <button 
                                v-if="devOps"
                                @click="setMode('devops')" 
                                :disabled="loading"
                                :class="['px-4 py-2 rounded-md text-sm font-medium transition-colors', mode === 'devops' ? 'bg-blue-600 text-white' : 'bg-gray-100 text-gray-700 hover:bg-gray-200', loading ? 'opacity-50 cursor-not-allowed' : '']"
                            >
                                Azure DevOps
                            </button>
                            <button 
                                v-if="imago"
                                @click="setMode('imago')" 
                                :disabled="loading"
                                :class="['px-4 py-2 rounded-md text-sm font-medium transition-colors', mode === 'imago' ? 'bg-blue-600 text-white' : 'bg-gray-100 text-gray-700 hover:bg-gray-200', loading ? 'opacity-50 cursor-not-allowed' : '']"
                            >
                                CRM
                            </button>
                        </div>
                        <!-- Mobile: select -->
                        <select
                            class="sm:hidden block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 text-sm p-2 border bg-white"
                            :value="mode"
                            :disabled="loading"
                            @change="setMode($event.target.value)"
                        >
                            <option value="activities">Rilevazioni</option>
                            <template v-if="peopleSmart">
                                <option value="pres">PS - Presenze</option>
                                <option value="gius">PS - Giustificativi</option>
                                <option value="anag">PS - Anagrafiche</option>
                            </template>
                            <option v-if="devOps" value="devops">Azure DevOps</option>
                            <option v-if="imago" value="imago">CRM</option>
                        </select>
                    </div>
                </div>

                <!-- Filters -->
                <div class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4 mb-6 items-end">
                    <div v-if="isAdmin" class="flex flex-col">
                        <label class="text-sm font-semibold text-gray-700 mb-1">Addetto</label>
                        <select v-model="filters.worker" @change="(mode === 'activities') ? loadActivities() : (mode === 'imago' ? loadImagoData() : null)" class="block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm p-2 border">
                            <option value="all">Tutti</option>
                            <option v-for="worker in workers" :key="worker.WorkerID" :value="worker.WorkerID">
                                {{ worker.LastName }} {{ worker.Name }}
                            </option>
                        </select>
                    </div>

                    <div v-if="mode === 'activities'" class="flex flex-col">
                        <label class="text-sm font-semibold text-gray-700 mb-1">Commessa</label>
                        <div class="relative w-full inline-flex items-center cursor-pointer" @click="loadJobs();">
                            <span class="block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm p-2.5 border bg-white truncate">
                                {{ filters.job ? filters.job : 'Tutte le commesse' }}
                            </span>
                            <span class="absolute right-3 text-gray-500 pointer-events-none">
                                <v-icon name="hi-search" scale="1.0" />
                            </span>
                        </div>
                    </div>

                    <div v-if="mode === 'activities'" class="flex flex-col">
                        <label class="text-sm font-semibold text-gray-700 mb-1">Operazione</label>
                        <select v-model="filters.operation" @change="loadActivities()" class="block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm p-2 border">
                            <option value="">Tutte le operazioni</option>
                            <option v-for="op in operations" :key="op.OperationID" :value="op.OperationID">
                                {{ op.Description }}
                            </option>
                        </select>
                    </div>

                    <div v-if="mode !== 'anag'" class="flex flex-col">
                        <label class="text-sm font-semibold text-gray-700 mb-1">Da data</label>
                        <input type="date" v-model="filters.dateFrom" @change="(mode === 'activities') ? loadActivities() : (mode === 'imago' ? loadImagoData() : null)" class="block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm p-2 border">
                    </div>

                    <div v-if="mode !== 'anag'" class="flex flex-col">
                        <label class="text-sm font-semibold text-gray-700 mb-1">A data</label>
                        <input type="date" v-model="filters.dateTo" @change="(mode === 'activities') ? loadActivities() : (mode === 'imago' ? loadImagoData() : null)" class="block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm p-2 border">
                    </div>
                </div>

                <div v-if="(mode === 'activities' && activityTableProps.totalTime) || (mode === 'pres' && presStats) || (mode === 'devops' && devopsStats)" class="mb-4 flex flex-wrap justify-end gap-4">
                    <div class="bg-blue-50 border border-blue-200 rounded-lg px-4 py-2 flex items-center gap-3">
                        <div class="text-blue-600">
                            <v-icon name="bi-clock-history" scale="1.2" />
                        </div>
                        <div>
                            <p class="text-[10px] text-blue-600 font-semibold uppercase tracking-wider leading-none mb-1">Tempo Totale</p>
                            <p class="text-xl font-bold text-blue-800 leading-none">
                                {{ mode === 'activities' ? activityTableProps.totalTime : (mode === 'devops' ? devopsStats.totalTime : presStats.totalTime) }}
                            </p>
                        </div>
                    </div>
                    <div v-if="((mode === 'activities' && activityTableProps.avgTime) || (mode === 'pres' && presStats) || (mode === 'devops' && devopsStats)) && !isSingleDay" class="bg-indigo-50 border border-indigo-200 rounded-lg px-4 py-2 flex items-center gap-3">
                        <div class="text-indigo-600">
                            <v-icon name="co-calendar" scale="1.2" />
                        </div>
                        <div>
                            <p class="text-[10px] text-indigo-600 font-semibold uppercase tracking-wider leading-none mb-1">Media totali giornalieri</p>
                            <p class="text-xl font-bold text-indigo-800 leading-none">
                                {{ mode === 'activities' ? activityTableProps.avgTime : (mode === 'devops' ? devopsStats.avgTime : presStats.avgTime) }}
                            </p>
                        </div>
                    </div>
                    <div v-if="((mode === 'activities' && activityTableProps.maxTime) || (mode === 'pres' && presStats) || (mode === 'devops' && devopsStats)) && !isSingleDay" class="bg-green-50 border border-green-200 rounded-lg px-4 py-2 flex items-center gap-3">
                        <div class="text-green-600">
                            <v-icon name="md-arrowupward" scale="1.2" />
                        </div>
                        <div>
                            <p class="text-[10px] text-green-600 font-semibold uppercase tracking-wider leading-none mb-1">
                                Massimo ({{ formatDate(mode === 'activities' ? activityTableProps.maxDate : (mode === 'devops' ? devopsStats.maxDate : presStats.maxDate)) }})
                            </p>
                            <p class="text-xl font-bold text-green-800 leading-none">
                                {{ mode === 'activities' ? activityTableProps.maxTime : (mode === 'devops' ? devopsStats.maxTime : presStats.maxTime) }}
                            </p>
                        </div>
                    </div>
                    <div v-if="((mode === 'activities' && activityTableProps.minTime) || (mode === 'pres' && presStats) || (mode === 'devops' && devopsStats)) && !isSingleDay" class="bg-orange-50 border border-orange-200 rounded-lg px-4 py-2 flex items-center gap-3">
                        <div class="text-orange-600">
                            <v-icon name="md-arrowdownward" scale="1.2" />
                        </div>
                        <div>
                            <p class="text-[10px] text-orange-600 font-semibold uppercase tracking-wider leading-none mb-1">
                                Minimo ({{ formatDate(mode === 'activities' ? activityTableProps.minDate : (mode === 'devops' ? devopsStats.minDate : presStats.minDate)) }})
                            </p>
                            <p class="text-xl font-bold text-orange-800 leading-none">
                                {{ mode === 'activities' ? activityTableProps.minTime : (mode === 'devops' ? devopsStats.minTime : presStats.minTime) }}
                            </p>
                        </div>
                    </div>
                </div>

                <!-- Activities Table -->
                <div class="overflow-x-auto">
                    <PageServerTable 
                        v-if="mode === 'activities' || mode === 'devops' || mode === 'imago'"
                        :headers="activityTableProps.headers" 
                        :items="mode === 'activities' || mode === 'imago' ? activityTableProps.items : externalFilteredItems"
                        :items-length="mode === 'activities' || mode === 'imago' ? activityTableProps.total : (mode === 'devops' ? devopsFilteredItems.length : externalFilteredItems.length)"
                        :items-per-page="activityTableProps.ipp"
                        :items-per-page-options="activityTableProps.ippo"
                        :page="activityTableProps.page"
                        :sort-by="activityTableProps.sortby"
                        :reload-fn="mode === 'activities' ? loadActivities : (mode === 'devops' ? loadDevOpsData : loadImagoData)"
                        :loading="loading"
                        hover
                        :class="mode === 'devops' && devopsWorkItemBaseUrl ? 'cursor-pointer' : ''"
                        @click:row="(e, { item }) => mode === 'devops' ? openDevOpsWorkItem(item) : null"
                    >
                        <template #item.PostingDate="{ item }">
                            {{ formatDate(item.PostingDate) }}
                        </template>
                        <template #item.DT__DATA="{ item }">
                            {{ formatDate(item.DT__DATA) }}
                        </template>
                        <template #item.ResolvedDate="{ item }">
                            {{ formatDate(item.ResolvedDate) }}
                        </template>
                        <template #item.ChangedDate="{ item }">
                            {{ formatDate(item.ChangedDate) }}
                        </template>
                        <template #item.date="{ item }">
                            {{ formatDate(item.date) }}
                        </template>
                        <template #item.minutes="{ item }">
                            {{ formatMinutes(item.minutes) }}
                        </template>
                        <template #item.workItemId="{ item }">
                            <div class="flex items-center gap-2">
                                <span :class="devopsWorkItemBaseUrl ? 'text-blue-600 hover:underline' : ''">{{ item.workItemId }}</span>
                                <button
                                    v-if="devopsWorkItemBaseUrl"
                                    @click.stop="loadWorkItemDetail(item)"
                                    class="p-0.5 text-gray-400 hover:text-blue-600 transition-colors"
                                    title="Dettagli Work Item"
                                >
                                    <v-icon name="bi-info-circle" scale="0.9" />
                                </button>
                            </div>
                        </template>
                        <template #item.DetailedTime="{ item }">
                            <span class="text-xs text-gray-600">{{ item.DetailedTime }}</span>
                        </template>
                        <template #no-data>
                            <div class="p-8 text-center text-gray-500">
                            Nessuna attività trovata per i filtri selezionati
                            </div>
                        </template>
                    </PageServerTable>

                    <PageNormalTable
                        v-else
                        :headers="activityTableProps.headers" 
                        :items="externalFilteredItems"
                        :loading="loading"
                        hover
                    >
                        <template #item.RFPERS_DATA_ASSZ="{ item }">
                            {{ formatDate(item.RFPERS_DATA_ASSZ) }}
                        </template>
                        <template #item.TORARI_PAG="{ item }">
                            {{ formatHHCC(item.TORARI_PAG) }}
                        </template>
                        <template #item.MES_LAV_TIME="{ item }">
                            <span :class="{'font-bold': item.MES_LAV_TIME !== item.TORARI_PAG}">
                                {{ formatHHCC(item.MES_LAV_TIME) }}
                            </span>
                        </template>
                        <template #item.MES_DATETIME="{ item }">
                            {{ formatDate(item.MES_DATETIME) }}
                        </template>
                        <template #item.QUANTITA_GIUSTIFICATIVO="{ item }">
                            <span :class="{
                                'text-green-600 font-semibold': item.MAGGIOR_PRESENZA_DESCR !== '******************************',
                                'text-red-600 font-semibold': item.MINOR_PRESENZA_DESCR !== '******************************'
                            }">
                                {{ formatHHCC(item.QUANTITA_GIUSTIFICATIVO) }}
                            </span>
                        </template>
                        <template #item.RFPERS_RAPPORTO_DI_LAVOROCOD="{ item }">
                            <span v-if="item.RFPERS_RAPPORTO_DI_LAVOROCOD == 0">Blank</span>
                            <span v-else-if="item.RFPERS_RAPPORTO_DI_LAVOROCOD == 1">Lavoro Dipendente</span>
                            <span v-else-if="item.RFPERS_RAPPORTO_DI_LAVOROCOD == 2">COCOCO</span>
                            <span v-else-if="item.RFPERS_RAPPORTO_DI_LAVOROCOD == 3">Coordinatore</span>
                            <span v-else-if="item.RFPERS_RAPPORTO_DI_LAVOROCOD == 4">Pensione</span>
                            <span v-else-if="item.RFPERS_RAPPORTO_DI_LAVOROCOD == 5">Amministratore</span>
                            <span v-else-if="item.RFPERS_RAPPORTO_DI_LAVOROCOD == 6">Erede</span>
                            <span v-else-if="item.RFPERS_RAPPORTO_DI_LAVOROCOD == 7">Collaboratore Progetto</span>
                            <span v-else>{{ item.RFPERS_RAPPORTO_DI_LAVOROCOD }}</span>
                        </template>
                        <template #item.VARIATION="{ item }">
                            <span v-if="item.MAGGIOR_PRESENZA_DESCR != '******************************'">{{ item.MAGGIOR_PRESENZA_DESCR }}</span>
                            <span v-else-if="item.MINOR_PRESENZA_DESCR != '******************************'">{{ item.MINOR_PRESENZA_DESCR }}</span>
                        </template>
                        <template #no-data>
                            <div class="p-8 text-center text-gray-500">
                            Nessun dato trovato per i filtri selezionati
                            </div>
                        </template>
                    </PageNormalTable>
                </div>
            </div>
        </div>

        <!-- Job Selection Modal -->
        <div v-if="showJobTable" class="fixed inset-0 flex items-center z-50 justify-center">
            <div class="absolute inset-0 bg-black opacity-50" @click="showJobTable = false"></div>
            <div class="bg-white rounded-lg shadow-xl z-10 max-h-[80vh] overflow-y-auto w-11/12">
                <div class="px-6 py-4 border-b flex items-center justify-between bg-gray-50">
                    <h2 class="text-xl font-bold text-gray-800 flex items-center gap-2">
                        <v-icon name="hi-search" class="text-blue-600" scale="1.2" />
                        Seleziona commessa
                    </h2>
                    <button @click="showJobTable = false" class="text-gray-500 hover:text-gray-700">
                        <v-icon name="md-close" />
                    </button>
                </div>
                <div class="p-6 pt-3">
                    <div class="mb-4">
                        <button @click="selectJob({Job: ''})" class="text-blue-600 hover:underline text-sm">
                            Tutte le commesse
                        </button>
                    </div>
                    <PageServerTable 
                        :headers="jobTableProps.headers" 
                        :items="jobTableProps.items"
                        :items-length="jobTableProps.total"
                        :items-per-page="jobTableProps.ipp"
                        :items-per-page-options="jobTableProps.ippo"
                        :page="jobTableProps.page"
                        :sort-by="jobTableProps.sortby"
                        :reload-fn="loadJobs"
                        hover
                        @click:row="(e, { item }) => selectJob(item)"
                    />
                </div>
            </div>
        </div>

        <!-- Work Item Detail Modal -->
        <div v-if="workItemModal.show" class="fixed inset-0 flex items-center z-50 justify-center">
            <div class="absolute inset-0 bg-black opacity-50" @click="workItemModal.show = false"></div>
            <div class="bg-white rounded-lg shadow-xl z-10 max-h-[85vh] overflow-y-auto w-11/12 max-w-3xl">
                <div class="px-6 py-4 border-b flex items-center justify-between bg-gray-50 sticky top-0">
                    <h2 class="text-lg font-bold text-gray-800 flex items-center gap-2">
                        <v-icon name="bi-info-circle" class="text-blue-600" />
                        Work Item
                        <span v-if="workItemModal.item" class="text-blue-600">#{{ workItemModal.item.id }}</span>
                    </h2>
                    <div class="flex items-center gap-3">
                        <a
                            v-if="workItemModal.item && devopsWorkItemBaseUrl"
                            :href="devopsWorkItemBaseUrl + workItemModal.item.id"
                            target="_blank"
                            class="text-sm text-blue-600 hover:underline flex items-center gap-1"
                        >
                            <v-icon name="bi-box-arrow-up-right" scale="0.85" />
                            Apri in DevOps
                        </a>
                        <button @click="workItemModal.show = false" class="text-gray-500 hover:text-gray-700">
                            <v-icon name="md-close" />
                        </button>
                    </div>
                </div>

                <div class="p-6">
                    <div v-if="workItemModal.loading" class="flex justify-center py-12">
                        <div class="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
                    </div>

                    <template v-else-if="workItemModal.item">
                        <div class="flex items-start gap-3 mb-6">
                            <span class="inline-block bg-blue-100 text-blue-800 text-xs font-semibold px-2.5 py-1 rounded">
                                {{ workItemModal.item.fields?.['System.WorkItemType'] }}
                            </span>
                            <span class="inline-block bg-gray-100 text-gray-700 text-xs font-semibold px-2.5 py-1 rounded">
                                {{ workItemModal.item.fields?.['System.State'] }}
                            </span>
                        </div>

                        <h3 class="text-xl font-semibold text-gray-900 mb-6">
                            {{ workItemModal.item.fields?.['System.Title'] }}
                        </h3>

                        <div class="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-6">
                            <div>
                                <p class="text-xs font-bold text-blue-500 mb-1">Assegnatario</p>
                                <p class="text-sm text-gray-800">{{ workItemModal.item.fields?.['System.AssignedTo']?.displayName ?? '—' }}</p>
                            </div>
                            <div>
                                <p class="text-xs font-bold text-blue-500 mb-1">Area</p>
                                <p class="text-sm text-gray-800">{{ workItemModal.item.fields?.['System.AreaPath'] ?? '—' }}</p>
                            </div>
                            <div>
                                <p class="text-xs font-bold text-blue-500 mb-1">Iterazione</p>
                                <p class="text-sm text-gray-800">{{ workItemModal.item.fields?.['System.IterationPath'] ?? '—' }}</p>
                            </div>
                            <div>
                                <p class="text-xs font-bold text-blue-500 mb-1">Ore completate</p>
                                <p class="text-sm text-gray-800">{{ workItemModal.item.fields?.['Microsoft.VSTS.Scheduling.CompletedWork'] ?? '—' }}</p>
                            </div>
                            <div>
                                <p class="text-xs font-bold text-blue-500 mb-1">Creato il</p>
                                <p class="text-sm text-gray-800">{{ formatDate(workItemModal.item.fields?.['System.CreatedDate']) }}</p>
                            </div>
                            <div>
                                <p class="text-xs font-bold text-blue-500 mb-1">Modificato il</p>
                                <p class="text-sm text-gray-800">{{ formatDate(workItemModal.item.fields?.['System.ChangedDate']) }}</p>
                            </div>
                            <div v-if="workItemModal.item.fields?.['Microsoft.VSTS.Common.ResolvedDate']">
                                <p class="text-xs font-bold text-blue-500 mb-1">Risolto il</p>
                                <p class="text-sm text-gray-800">{{ formatDate(workItemModal.item.fields?.['Microsoft.VSTS.Common.ResolvedDate']) }}</p>
                            </div>
                            <div v-if="workItemModal.item.fields?.['System.Tags']">
                                <p class="text-xs font-bold text-blue-500 mb-1">Tag</p>
                                <p class="text-sm text-gray-800">{{ workItemModal.item.fields?.['System.Tags'] }}</p>
                            </div>
                        </div>

                        <div v-if="workItemModal.item.fields?.['System.Description']" class="border-t pt-4">
                            <p class="text-sm font-bold text-blue-500 mb-3">Descrizione</p>
                            <div
                                class="prose prose-sm max-w-none text-gray-700"
                                v-html="workItemModal.item.fields?.['System.Description']"
                            ></div>
                        </div>

                        <div v-if="workItemModal.item.fields?.['Microsoft.VSTS.TCM.ReproSteps']" class="border-t pt-4 mt-4">
                            <p class="text-sm font-bold text-blue-500 mb-3">Passi per la riproduzione</p>
                            <div
                                class="prose prose-sm max-w-none text-gray-700"
                                v-html="workItemModal.item.fields?.['Microsoft.VSTS.TCM.ReproSteps']"
                            ></div>
                        </div>

                        <div class="border-t pt-4 mt-4">
                            <p class="text-sm font-bold text-blue-500 mb-3">
                                Timelog ({{ workItemTimeLogs.length }})
                            </p>
                            <div v-if="workItemTimeLogs.length === 0" class="text-sm text-gray-400 italic">
                                Nessun record timelog trovato per questo work item.
                            </div>
                            <table v-else class="w-full text-sm text-left">
                                <thead>
                                    <tr class="border-b">
                                        <th class="pb-2 pr-4 text-xs font-bold text-blue-500">Utente</th>
                                        <th class="pb-2 pr-4 text-xs font-bold text-blue-500">Data</th>
                                        <th class="pb-2 pr-4 text-xs font-bold text-blue-500">Commento</th>
                                        <th class="pb-2 text-right text-xs font-bold text-blue-500">Durata</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    <tr v-for="log in workItemTimeLogs" :key="log.id" class="border-b last:border-0 hover:bg-gray-50">
                                        <td class="py-2 pr-4 text-gray-700">{{ log.user }}</td>
                                        <td class="py-2 pr-4 text-gray-700 whitespace-nowrap">{{ formatDate(log.date) }}</td>
                                        <td class="py-2 pr-4 text-gray-600">{{ log.comment }}</td>
                                        <td class="py-2 text-right text-gray-700 whitespace-nowrap font-mono">{{ formatMinutes(log.minutes) }}</td>
                                    </tr>
                                </tbody>
                                <tfoot>
                                    <tr class="border-t">
                                        <td colspan="3" class="pt-2 text-xs font-bold text-blue-500">Totale</td>
                                        <td class="pt-2 text-right font-bold text-gray-800 font-mono whitespace-nowrap">
                                            {{ formatMinutes(workItemTimeLogs.reduce((s, i) => s + (i.minutes || 0), 0)) }}
                                        </td>
                                    </tr>
                                </tfoot>
                            </table>
                        </div>
                    </template>
                </div>
            </div>
        </div>

        <ToastNotification
            :show="toast.show"
            :message="toast.message"
            :type="toast.type"
            @update:show="toast.show = $event"
        />
    </div>
</template>

<script>
import { Link } from '@inertiajs/vue3';
import BreadCrumbs from '@/Components/BreadCrumbs.vue';
import PageServerTable from '@/Components/PageServerTable.vue';
import PageNormalTable from '@/Components/PageNormalTable.vue';
import ToastNotification from '@/Components/ToastNotification.vue';
import axios from 'axios';

export default {
    components: {
        Link,
        BreadCrumbs,
        PageServerTable,
        PageNormalTable,
        ToastNotification
    },
    props: {
        allowDetailedTime: Boolean,
        workers: Array,
        operations: Array,
        allowSearchJobs: Boolean,
        peopleSmart: Boolean,
        devOps: Boolean,
        devopsWorkItemBaseUrl: String,
        devopsWorkerMap: Object,
        imago: Boolean
    },
    data() {
        return {
            loading: false,
            showJobTable: false,
            mode: 'activities', // 'activities', 'pres', 'gius', 'anag', 'devops', 'imago'
            filters: {
                worker: 'all',
                job: '',
                operation: '',
                dateFrom: new Date().toISOString().split('T')[0],
                dateTo: new Date().toISOString().split('T')[0],
            },
            activityTableProps: {
                headers: [],
                items: [],
                total: 0,
                totalTime: '00:00',
                avgTime: '00:00',
                minTime: '00:00',
                minDate: '',
                maxTime: '00:00',
                maxDate: '',
                ipp: 25,
                ippo: [
                    { "value": 10, "title": '10' },
                    { "value": 25, "title": '25' },
                    { "value": 50, "title": '50' }
                ],
                page: 1,
                sortby: []
            },
            jobTableProps: {
                headers: [
                    { title: 'Commessa', align: 'start', value: 'Job', key: 'MA_Jobs.Job', filterable: true, filtervalue: "" },
                    { title: 'Descrizione', align: 'start', value: 'Description', key: 'MA_Jobs.Description', filterable: true, filtervalue: "" },
                    { title: 'Ragione Sociale', align: 'start', value: 'RagSoc', key: 'MA_CustSupp.CompanyName', filterable: true, filtervalue: "" }
                ],
                items: [],
                total: 0,
                ipp: 10,
                ippo: [
                    { "value": 10, "title": '10' },
                    { "value": 25, "title": '25' },
                    { "value": 50, "title": '50' }
                ],
                page: 1,
                sortby: [{ key: 'MA_Jobs.Job', order: 'asc' }]
            },
            toast: {
                show: false,
                message: '',
                type: 'success'
            },
            workItemModal: {
                show: false,
                loading: false,
                item: null
            }
        };
    },
    computed: {
        isAdmin() {
            return this.$page.props.isAdminOrSuperAdmin;
        },
        isSingleDay() {
            return this.filters.dateFrom && this.filters.dateTo && this.filters.dateFrom === this.filters.dateTo;
        },
        navigation() {
            return [
                {
                    name: 'Dashboard',
                    href: route('dashboard'),
                },
                {
                    name: this.$t('Rilevazione Commesse'),
                    href: route("modules.home", "rilevazione-commesse"),
                },
                {
                    name: this.$t('Storico Rilevazioni'),
                    href: route('modules.rcomm.storico-rilevazioni'),
                }
            ];
        },
        workItemTimeLogs() {
            if (!this.workItemModal.item) return [];
            return [...this.activityTableProps.items]
                .filter(i => i.workItemId == this.workItemModal.item.id)
                .sort((a, b) => a.date < b.date ? -1 : a.date > b.date ? 1 : 0);
        },
        devopsFilteredItems() {
            if (this.mode !== 'devops') return [];
            let items = [...this.activityTableProps.items];
            if (this.filters.dateFrom) items = items.filter(i => i.date >= this.filters.dateFrom);
            if (this.filters.dateTo) items = items.filter(i => i.date <= this.filters.dateTo);
            if (this.filters.worker && this.filters.worker !== 'all' && this.devopsWorkerMap) {
                const devopsUser = this.devopsWorkerMap[this.filters.worker];
                if (devopsUser) {
                    items = items.filter(i => i.user === devopsUser);
                } else {
                    items = [];
                }
            }
            const sortby = this.activityTableProps.sortby;
            if (sortby && sortby.length > 0) {
                const { key, order } = sortby[0];
                items.sort((a, b) => {
                    const av = a[key] ?? '';
                    const bv = b[key] ?? '';
                    if (av < bv) return order === 'asc' ? -1 : 1;
                    if (av > bv) return order === 'asc' ? 1 : -1;
                    return 0;
                });
            }
            return items;
        },
        devopsStats() {
            if (this.mode !== 'devops') return null;
            const items = this.devopsFilteredItems;
            if (items.length === 0) return null;

            const dailyMinutes = {};
            items.forEach(item => {
                const d = item.date;
                if (!dailyMinutes[d]) dailyMinutes[d] = 0;
                dailyMinutes[d] += item.minutes || 0;
            });

            const dailyValues = Object.values(dailyMinutes);
            const totalMinutes = dailyValues.reduce((s, v) => s + v, 0);
            const avgMinutes = totalMinutes / dailyValues.length;
            const maxMinutes = Math.max(...dailyValues);
            const minMinutes = Math.min(...dailyValues);
            const maxDate = Object.keys(dailyMinutes).find(d => dailyMinutes[d] === maxMinutes);
            const minDate = Object.keys(dailyMinutes).find(d => dailyMinutes[d] === minMinutes);

            const fmt = (m) => `${Math.floor(m / 60)}:${String(Math.round(m % 60)).padStart(2, '0')}`;

            return {
                totalTime: fmt(totalMinutes),
                avgTime: fmt(avgMinutes),
                maxTime: fmt(maxMinutes),
                maxDate,
                minTime: fmt(minMinutes),
                minDate
            };
        },
        externalFilteredItems() {
            if (this.mode === 'activities' || this.mode === 'imago') return this.activityTableProps.items;
            else if (this.mode === 'devops') {
                const filtered = this.devopsFilteredItems;
                return filtered.slice(this.activityTableProps.page * this.activityTableProps.ipp - this.activityTableProps.ipp, this.activityTableProps.page * this.activityTableProps.ipp);
            }
            else {
            
                const from = this.filters.dateFrom ? this.filters.dateFrom : null;
                const to = this.filters.dateTo ? this.filters.dateTo : null;

                const fromInt = from ? from.replace(/-/g, '') : null;
                const toInt = to ? to.replace(/-/g, '') : null;
                
                let items = this.activityTableProps.items.filter(item => {
                    if (this.mode === 'anag' ) return true;

                    const itemDate = item.MES_DATETIME;
                    if (!itemDate) return true;
                    
                    if (fromInt && itemDate < fromInt) return false;
                    if (toInt && itemDate > toInt) return false;
                    
                    return true;
                });

                if (this.filters.worker !== 'all') {
                    const selectedWorker = this.workers.find(w => w.WorkerID == this.filters.worker);
                    if (selectedWorker) {
                        const workerName = (selectedWorker.LastName + ' ' + selectedWorker.Name).toLowerCase();
                        
                        items = items.filter(item => {
                            if (this.mode === 'pres' || this.mode === 'gius') {
                                const val = (item.MES_DIP || '').toLowerCase();
                                return val.includes(workerName);
                            }
                            if (this.mode === 'anag') {
                                const val = (item.RFPERS_MATRICOLA || '').toLowerCase();
                                return val.includes(workerName);
                            }
                            if (this.mode === 'imago') {
                                const val = (item.IdOp || '').toLowerCase();
                                return val.includes(workerName);
                            }
                            return true;
                        });
                    }
                }

                return items;
            }
        },
        presStats() {
            if (this.mode !== 'pres') return null;
            
            const items = this.externalFilteredItems;
            if (items.length === 0) return null;

            let totalMinutes = 0;
            const dailyMinutes = {};

            items.forEach(item => {
                const val = parseFloat(item.MES_LAV_TIME || 0);
                const hours = Math.floor(val / 100);
                const minutes = Math.round((val / 100 - hours) * 60);
                const itemTotalMinutes = hours * 60 + minutes;
                
                totalMinutes += itemTotalMinutes;

                const date = item.MES_DATETIME;
                if (!dailyMinutes[date]) dailyMinutes[date] = 0;
                dailyMinutes[date] += itemTotalMinutes;
            });

            const dailyValues = Object.values(dailyMinutes);
            const daysCount = dailyValues.length;
            
            const maxMinutes = daysCount > 0 ? Math.max(...dailyValues) : 0;
            const minMinutes = daysCount > 0 ? Math.min(...dailyValues) : 0;
            const avgMinutes = daysCount > 0 ? totalMinutes / daysCount : 0;

            const format = (mins) => {
                const h = Math.floor(mins / 60);
                const m = Math.round(mins % 60);
                return `${h.toString().padStart(2, '0')}:${m.toString().padStart(2, '0')}`;
            };

            const maxDate = Object.keys(dailyMinutes).find(date => dailyMinutes[date] === maxMinutes);
            const minDate = Object.keys(dailyMinutes).find(date => dailyMinutes[date] === minMinutes);

            return {
                totalTime: format(totalMinutes),
                avgTime: format(avgMinutes),
                maxTime: format(maxMinutes),
                maxDate: maxDate,
                minTime: format(minMinutes),
                minDate: minDate
            };
        }
    },
    methods: {
        showToast(message, type = 'success') {
            this.toast.message = message;
            this.toast.type = type;
            this.toast.show = true;
        },
        setMode(mode) {
            this.mode = mode;
            this.updateHeaders();
            if (mode === 'activities') {
                this.loadActivities();
            } else if (mode === 'devops') {
                this.loadDevOpsData();
            } else if (mode === 'imago') {
                this.loadImagoData();
            } else {
                this.loadPeopleSmartData();
            }
        },
        updateHeaders() {
            if (this.mode === 'activities') {
                this.activityTableProps.headers = [];
                if (this.isAdmin) {
                    this.activityTableProps.headers.push({ title: 'Addetto', key: 'User', value: 'User', align: 'start', sortable: true });
                }
                this.activityTableProps.headers.push({ title: 'Data', key: 'PostingDate', value: 'PostingDate', align: 'start', sortable: true });
                this.activityTableProps.headers.push({ title: 'Commessa', key: 'Job', value: 'Job', align: 'start', sortable: true});
                this.activityTableProps.headers.push({ title: 'Operazione', key: 'Description', value: 'Description', align: 'start', sortable: true});
                this.activityTableProps.headers.push({ title: 'Tempo', key: 'Time', value: 'Time', align: 'end', sortable: true });

                if (this.allowDetailedTime) {
                    this.activityTableProps.headers.push({ title: 'Orario', key: 'DetailedTime', value: 'DetailedTime', align: 'end', sortable: true });
                }

                this.activityTableProps.headers.push({ title: 'Note', key: 'OperationNote', value: 'OperationNote', align: 'start' });
            } else if (this.mode === 'pres') {
                this.activityTableProps.headers = [
                    { title: 'Matricola (Addetto)', key: 'MES_DIP', align: 'start', filterable: true, filtervalue: "" },
                    { title: 'Chiave PBI3', key: 'PBI3_KEY', align: 'start', filterable: true, filtervalue: "" },   
                    { title: 'Data', key: 'MES_DATETIME', align: 'start' },
                    { title: 'Azienda', key: 'MES_SOC', align: 'start', filterable: true, filtervalue: "" },
                    { title: 'Ore Teoriche', key: 'TORARI_PAG', align: 'end', filterable: true, filtervalue: "" },
                    { title: 'Ore Lavorate', key: 'MES_LAV_TIME', align: 'end', filterable: true, filtervalue: "" }
                ];
            } else if (this.mode === 'gius') {
                this.activityTableProps.headers = [
                    { title: 'Matricola (Addetto)', key: 'MES_DIP', align: 'start', filterable: true, filtervalue: "" },
                    { title: 'Data', key: 'MES_DATETIME', align: 'start' },
                    { title: 'Codice', key: 'MES_CODICE_GIUSTIFICATIVO', align: 'start', filterable: true, filtervalue: "" },
                    { title: 'Causale', key: 'ICODCAU_DESCR', align: 'start', filterable: true, filtervalue: "" },
                    //{ title: 'Maggior Presenza', key: 'MAGGIOR_PRESENZA_DESCR', align: 'start', filterable: true, filtervalue: "" },
                    //{ title: 'Minor Presenza', key: 'MINOR_PRESENZA_DESCR', align: 'start', filterable: true, filtervalue: "" },
                    { title: 'Variazione', key: 'VARIATION', align: 'start', filterable: true, filtervalue: "" },
                    { title: 'Trasferte/Viaggio', key: 'TRASFERTE_OREVIAGGIO_DESCR', align: 'start', filterable: true, filtervalue: "" },
                    { title: 'Ore', key: 'QUANTITA_GIUSTIFICATIVO', align: 'end', filterable: true, filtervalue: "" }
                ];
            } else if (this.mode === 'anag') {
                this.activityTableProps.headers = [
                    { title: 'Matricola (Addetto)', key: 'RFPERS_MATRICOLA', align: 'start', filterable: true, filtervalue: "" },
                    //{ title: 'Codice centro di costo', key: 'RFPERS_CDC', align: 'start', filterable: true, filtervalue: "" },                 
                    { title: 'Cognome', key: 'RFPERS_COGN', align: 'start', filterable: true, filtervalue: "" },
                    { title: 'Nome', key: 'RFPERS_NOME', align: 'start', filterable: true, filtervalue: "" },
                    { title: 'Cod. Fiscale', key: 'RFPERS_FISCALE', align: 'start', filterable: true, filtervalue: "" },
                    { title: 'Qualifica', key: 'TQUAL_DESCRIZ', align: 'start', filterable: true, filtervalue: "" },
                    { title: 'Sesso', key: 'RFPERS_SESSO', align: 'start', filterable: true, filtervalue: "" },
                    { title: 'Assunzione', key: 'RFPERS_DATA_ASSZ', align: 'start', filterable: true, filtervalue: "" },
                    { title: 'Tipo rapporto', key: 'RFPERS_RAPPORTO_DI_LAVOROCOD', align: 'start', filterable: true, filtervalue: "" }           
                ];
            } else if (this.mode === 'devops') {
                this.activityTableProps.headers = [
                    { title: 'Utente', key: 'user', value: 'user', align: 'start', sortable: true },
                    { title: 'Data', key: 'date', value: 'date', align: 'start', sortable: true },
                    { title: 'Work Item', key: 'workItemId', value: 'workItemId', align: 'start', sortable: true },
                    { title: 'Tipo', key: 'type', value: 'type', align: 'start', sortable: true },
                    { title: 'Commento', key: 'comment', value: 'comment', align: 'start', sortable: false },
                    { title: 'Durata', key: 'minutes', value: 'minutes', align: 'end', sortable: true }
                ];
            } else if (this.mode === 'imago') {
                this.activityTableProps.headers = [
                    { title: 'Addetto', key: 'IdOp', align: 'start', filterable: true, filtervalue: "" },
                    { title: 'Data', key: 'DT__DATA', align: 'start' },
                    { title: 'Cliente', key: 'cod_cli', align: 'start', filterable: true, filtervalue: "" },
                    { title: 'Ticket', key: 'numero_ticket', align: 'start', filterable: true, filtervalue: "" },
                    { title: 'Oggetto', key: 'Oggetto', align: 'start', filterable: true, filtervalue: "" },
                    { title: 'Tempo', key: 'tempo_riga', align: 'end' },
                    { title: 'Stato', key: 'TT_STATO', align: 'start' }
                ];
            }

            this.activityTableProps.sortby = [];
            this.activityTableProps.items = [];
            this.activityTableProps.page = 1;
            this.activityTableProps.ipp = 25;
        },
        async loadPeopleSmartData() {
            this.loading = true;

            try {
                const endpoint = `modules.rcomm.peoplesmart-${this.mode}`;
                const res = await axios.post(route(endpoint));
                
                if (res.data.success) {
                    this.activityTableProps.items = res.data.items;
                    this.activityTableProps.total = res.data.total;
                    this.activityTableProps.page = 1;
                } else {
                    this.showToast(res.data.errorMessage || 'Errore PeopleSmart', 'error');
                }
            } catch (e) {
                this.showToast(e.response?.data?.errorMessage || this.$t('Errore durante il caricamento dei dati PeopleSmart'), 'error');
            } finally {
                this.loading = false;
            }
        },
        async loadImagoData(sortby, itemsperpage, page) {
            this.loading = true;
            try {

                if (sortby) this.activityTableProps.sortby = sortby;
                if (itemsperpage) this.activityTableProps.ipp = itemsperpage;
                if (page) this.activityTableProps.page = page;

                const res = await axios.post(route('modules.rcomm.get-imago-data'), {
                    dateFrom: this.filters.dateFrom,
                    dateTo: this.filters.dateTo,
                    worker: this.filters.worker,
                    sortby: Array.isArray(sortby) ? sortby : this.activityTableProps.sortby,
                    headers: this.activityTableProps.headers,
                    itemsperpage: typeof itemsperpage === 'number' ? itemsperpage : this.activityTableProps.ipp,
                    page: typeof page === 'number' ? page : this.activityTableProps.page
                });
                
                if (res.data.success) {
                    this.activityTableProps.items = res.data.items;
                    this.activityTableProps.total = res.data.total;
                    this.activityTableProps.ipp = res.data.ipp;
                    this.activityTableProps.page = res.data.page;
                    this.activityTableProps.headers = res.data.headers || this.activityTableProps.headers;
                    this.activityTableProps.sortby = res.data.sortby || this.activityTableProps.sortby;
                } else {
                    this.showToast(res.data.errorMessage || 'Errore CRM', 'error');
                }
            } catch (e) {
                this.showToast(e.response?.data?.errorMessage || this.$t('Errore durante il caricamento dei dati CRM'), 'error');
            } finally {
                this.loading = false;
            }
        },
        async loadDevOpsData(sortby, itemsperpage, page) {
            if (sortby) this.activityTableProps.sortby = Array.isArray(sortby) ? sortby : this.activityTableProps.sortby;
            if (typeof itemsperpage === 'number') this.activityTableProps.ipp = itemsperpage;
            if (typeof page === 'number') this.activityTableProps.page = page;

            if (this.activityTableProps.items.length > 0 && (sortby || itemsperpage || page)) {
                return;
            }

            this.loading = true;

            try {
                const res = await axios.get(route('modules.rcomm.get-devops-extension-data'));
                
                if (res.data.success) {
                    this.activityTableProps.items = res.data.items;
                } else {
                    this.showToast(res.data.errorMessage || 'Errore Azure DevOps', 'error');
                }
            } catch (e) {
                this.showToast(e.response?.data?.errorMessage || this.$t('Errore durante il caricamento dei dati Azure DevOps'), 'error');
            } finally {
                this.loading = false;
            }
        },
        async loadActivities(sortby, itemsperpage, page) {
            this.loading = true;
            
            try {

                if (sortby) this.activityTableProps.sortby = sortby;
                if (itemsperpage) this.activityTableProps.ipp = itemsperpage;
                if (page) this.activityTableProps.page = page;

                const res = await axios.post(route('modules.rcomm.get-activities'), {
                    ...this.filters,
                    headers: this.activityTableProps.headers,
                    sortby: Array.isArray(sortby) ? sortby : this.activityTableProps.sortby,
                    itemsperpage: typeof itemsperpage === 'number' ? itemsperpage : this.activityTableProps.ipp,
                    page: typeof page === 'number' ? page : this.activityTableProps.page
                });
                
                if (res.data.success) {

                    this.activityTableProps.items = res.data.activities;
                    this.activityTableProps.total = res.data.total;
                    this.activityTableProps.totalTime = res.data.totalTime;
                    this.activityTableProps.avgTime = res.data.avgTime;
                    this.activityTableProps.minTime = res.data.minTime;
                    this.activityTableProps.minDate = res.data.minDate;
                    this.activityTableProps.maxTime = res.data.maxTime;
                    this.activityTableProps.maxDate = res.data.maxDate;
                    this.activityTableProps.ipp = res.data.ipp;
                    this.activityTableProps.page = res.data.page;
                    this.activityTableProps.headers = res.data.headers;
                    this.activityTableProps.sortby = res.data.sortby;
                }
            } catch (e) {
                this.showToast(this.$t('Errore durante il caricamento delle attività'), 'error');
            } finally {
                this.loading = false;
            }
        },
        async loadJobs(sortby, itemsperpage, page) {
            try {
                const res = await axios.post(route('modules.rcomm.get-jobs'), {
                    headers: this.jobTableProps.headers,
                    sortby: Array.isArray(sortby) ? sortby : this.jobTableProps.sortby,
                    itemsperpage: typeof itemsperpage === 'number' ? itemsperpage : this.jobTableProps.ipp,
                    page: typeof page === 'number' ? page : this.jobTableProps.page
                });
                
                if (res.data.success) {
                    this.jobTableProps.items = res.data.jobs;
                    this.jobTableProps.total = res.data.total;
                    this.jobTableProps.ipp = res.data.ipp;
                    this.jobTableProps.page = res.data.page;
                    this.jobTableProps.headers = res.data.headers;
                    this.jobTableProps.sortby = res.data.sortby;
                }

                this.showJobTable = true;
            } catch (e) {
                console.error("Errore caricamento commesse:", e);
            }
        },
        selectJob(job) {
            this.filters.job = job.Job;
            this.showJobTable = false;
            if (this.mode === 'activities') {
                this.loadActivities();
            } else {
                this.loadPeopleSmartData();
            }
        },
        formatDate(date) {
            if (!date) return '';
            
            // Handle YYYYMMDD format (PeopleSmart)
            if (typeof date === 'string' && date.length === 8 && /^\d+$/.test(date)) {
                const y = date.substring(0, 4);
                const m = date.substring(4, 6);
                const d = date.substring(6, 8);
                return `${d}/${m}/${y}`;
            }
            
            return new Date(date).toLocaleDateString('it-IT');
        },
        openDevOpsWorkItem(item) {
            if (this.devopsWorkItemBaseUrl && item.workItemId) {
                window.open(this.devopsWorkItemBaseUrl + item.workItemId, '_blank');
            }
        },
        async loadWorkItemDetail(item) {
            this.workItemModal.show = true;
            this.workItemModal.loading = true;
            this.workItemModal.item = null;
            try {
                const res = await axios.get(route('modules.rcomm.get-devops-workitem', { id: item.workItemId }));
                if (res.data.success) {
                    this.workItemModal.item = res.data.item;
                } else {
                    this.showToast(res.data.errorMessage || 'Errore caricamento Work Item', 'error');
                    this.workItemModal.show = false;
                }
            } catch (e) {
                this.showToast(e.response?.data?.errorMessage || 'Errore caricamento Work Item', 'error');
                this.workItemModal.show = false;
            } finally {
                this.workItemModal.loading = false;
            }
        },
        formatMinutes(minutes) {
            if (!minutes && minutes !== 0) return '';
            const h = Math.floor(minutes / 60);
            const m = minutes % 60;
            return `${h}:${m.toString().padStart(2, '0')}`;
        },
        formatHHCC(value) {
            if (!value || isNaN(value)) return '00:00';
            const num = parseFloat(value);
            const totalHours = num / 100;
            const hours = Math.floor(totalHours);
            const minutes = Math.round((totalHours - hours) * 60);
            return `${hours.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')}`;
        }
    },
    mounted() {
        if (this.$page.props.isAdminOrSuperAdmin) {
            this.filters.worker = 'all';
        } else {
            this.filters.worker = this.$page.props.auth.user.worker;
        }

        this.updateHeaders();
        this.loadActivities();
    }
};
</script>
